import { computed, defineComponent, reactive, ref, watch } from "vue"

import { axiosAuthBackgroundInstance, AxiosErrorWrapper, axiosInstance } from "src/boot/axios";
import { events } from "src/store/EventuallyPinia"

import { BulkSelectedEventSignupTracker, ExpandedEvent } from "./R_EventRosterImpl.ilx"

import EventRosterImpl from "./R_EventRosterImpl.vue";
import { escapeRegExp, parseIntOrFail, requireNonNull, SetEx, sortByAccentAwareCaseInsenstive, UiOption, uniqueBy, vReqT } from "src/helpers/utils";
import { Guid, Int, Integerlike } from "src/interfaces/InleagueApiV1";
import { addLabelsToEventSignups, EventSignup, removeLabelsFromEventSignups, updateLabelsForEventSignup, updateSignup } from "src/composables/InleagueApiV1.Event";
import { AutoModal, DefaultModalController_r, DefaultTinySoccerballBusyOverlay, ModalUtil, useDefaultNoCloseModalIfBusy } from "../UserInterface/Modal";
import { AddEventSignupLabelsModal, BulkAddPlayersAsEligibleToRegisterModal, BulkSelectByLabelModal, EditSignupCommentsModal, RemoveEventSignupLabelsModal, UpdateEventSignupLabelsModal } from "./EventRoster.modals";
import { bulkAddPlayersAsEligibleToRegister } from "src/composables/InleagueApiV1.Registration";
import iziToast from "izitoast";

export default defineComponent({
  props: {
    eventID: vReqT<Guid>(),
  },
  setup(props) {
    const expandedEvent = ref<ExpandedEvent | null>(null);
    const includeCanceledSignups = ref(false)
    const highlightedEventSignups = ref(new Set<Guid>())

    const includeIncompleteSignups = ref(false);
    const adultOrPlayerNameInput = reactive({
      input: "",
      committed: ""
    })

    const labelFilter = (() => {
      const options = computed<UiOption<Integerlike>[]>(() => {
        const options = expandedEvent
          .value
          ?.signups
          .flatMap(v => v.labels)
          .filter(uniqueBy(v => v.eventSignupLabelID)) ?? []

        return options
          .sort(sortByAccentAwareCaseInsenstive(v => v.label))
          .map((v) : UiOption<Integerlike> => ({label: v.label, value: v.eventSignupLabelID}))
      })

      const selections = ref<Integerlike[]>([])

      watch(() => options.value, () => {
        // update selections as appropriate when the listing of available options changes
        const optset = new Set<Int>(options.value.map(v => parseIntOrFail(v.value)))
        selections.value = selections.value.filter(sel => optset.has(parseIntOrFail(sel)))
      }, {deep: false})

      return {
        options,
        selections,
      }
    })();

    const filteredSignups = computed(() => {
      return applyLabelFilter(
        applyIncludeIncompleteSignups(
          applyNameFilter(expandedEvent.value?.signups ?? [])
        )
      );

      function applyIncludeIncompleteSignups(vs: EventSignup[]) : EventSignup[] {
        if (includeIncompleteSignups.value) {
          // include incomplete currently means "show everything we have",
          // because we pull all non-canceled (complete or incomplete) signups from the backend
          return vs;
        }
        else {
          // DO NOT include incomplete signups
          // as in, only show those that are "paid=true"
          return vs.filter(signup => !!signup.paid)
        }
      }

      function applyNameFilter(vs: EventSignup[]) : EventSignup[] {
        if (adultOrPlayerNameInput.committed.trim()) {
          const pattern = new RegExp(escapeRegExp(adultOrPlayerNameInput.committed.trim()), "i");
          return vs.filter(signup => {
            const name = signup.childID
              ? `${signup.childFirstName} ${signup.childLastName}`
              : `${signup.userFirstName} ${signup.userLastName}`;
            return pattern.test(name);
          });
        }
        else {
          return vs;
        }
      }

      function applyLabelFilter(signups: EventSignup[]) : EventSignup[] {
        const selected = new Set<Int>(labelFilter.selections.value.map(v => parseIntOrFail(v)))

        if (selected.size === 0) {
          return signups;
        }

        return signups.filter(signup => {
          return signup.labels.some(label => selected.has(label.eventSignupLabelID))
        })
      }
    })

    const bulkSelectedEventSignups = (() => {
      const selected = ref(new Map<string, EventSignup>())

      const reset = () : void => {
        selected.value = new Map()
      }

      const allSelected = computed<boolean>(() => {
        if (!expandedEvent.value) {
          return false
        }
        return filteredSignups.value.length === selected.value.size
      })

      const toggleSelectAll = () : void => {
        if (!expandedEvent.value) {
          return
        }

        if (allSelected.value) {
          selected.value = new Map()
        }
        else {
          selected.value = new Map()

          for (const v of filteredSignups.value) {
            selected.value.set(v.eventSignupID, v)
          }
        }
      }

      return {
        reset,
        get allSelected() { return allSelected.value },
        toggleSelectAll,
        get selected() { return selected.value }
      }
    })() satisfies BulkSelectedEventSignupTracker;

    const bulkSelectionsNotContainedInFilteredSignups = computed(() => {
      if (bulkSelectedEventSignups.selected.size === 0) {
        return 0
      }

      let count = 0

      const allSignupIDs = new Set(expandedEvent.value?.signups.map(v => v.eventSignupID) ?? [])
      const retained = new Set(filteredSignups.value.map(v => v.eventSignupID) ?? [])
      const filteredAway = new SetEx(allSignupIDs).subtract(retained)

      for (const eventSignupID of filteredAway) {
        if (bulkSelectedEventSignups.selected.has(eventSignupID)) {
          count++
        }
      }

      return count
    })

    const rehydrateFromCurrentConfig = async () : Promise<void> => {
      expandedEvent.value = null;
      bulkSelectedEventSignups.reset()

      expandedEvent.value = (await events.get(axiosInstance, props.eventID, {
        includeCanceledSignups: includeCanceledSignups.value,
        expand: ["signups","rosterLayoutDetail", "signups.labels"]
      })) as ExpandedEvent
    };

    watch(() => props.eventID, rehydrateFromCurrentConfig, {immediate: true});

    const bulkAddLabelsModalController = (() => {
      const busy = ref(false)

      const handleSubmit = async (signups: EventSignup[], eventLabelIDs: Integerlike[]) : Promise<void> => {
        if (signups.length === 0 || eventLabelIDs.length === 0) {
          return;
        }

        try {
          try {
            busy.value = true
            const result = await addLabelsToEventSignups(axiosAuthBackgroundInstance, {
              eventSignupIDs: signups.map(v => v.eventSignupID),
              eventSignupLabelIDs: eventLabelIDs
            })

            for (const signup of signups) {
              signup.labels = requireNonNull(result[signup.eventSignupID])
            }
          }
          finally {
            busy.value = false;
          }
          highlightedEventSignups.value.clear()
          bulkAddLabelsModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<EventSignup[]>({
        title: ModalUtil.simpleTitle("Add Labels"),
        content: signups => {
          if (!signups) {
            return null
          }

          return <div>
            <AddEventSignupLabelsModal
              eventID={requireNonNull(expandedEvent.value?.eventID)}
              eventSignups={signups}
              onSubmit={args => handleSubmit(signups, args.eventLabelIDs)}
              onCancel={() => bulkAddLabelsModalController.close()}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB: (cb) => {
        if (!busy.value) {
          highlightedEventSignups.value.clear()
          cb()
        }
      }})
    })()

    const bulkRemoveLabelsModalController = (() => {
      const busy = ref(false)

      const handleSubmit = async (signups: EventSignup[], eventLabelIDs: Integerlike[]) : Promise<void> => {
        if (signups.length === 0 || eventLabelIDs.length === 0) {
          return;
        }

        try {
          try {
            busy.value = true
            const result = await removeLabelsFromEventSignups(axiosAuthBackgroundInstance, {
              eventSignupIDs: signups.map(v => v.eventSignupID),
              eventSignupLabelIDs: eventLabelIDs
            })

            for (const signup of signups) {
              signup.labels = requireNonNull(result[signup.eventSignupID])
            }
          }
          finally {
            busy.value = false;
          }
          bulkRemoveLabelsModalController.close()
          highlightedEventSignups.value.clear()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<EventSignup[]>({
        title: ModalUtil.simpleTitle("Remove Labels"),
        content: signups => {
          if (!signups) {
            return null
          }

          return <div>
            <RemoveEventSignupLabelsModal
              eventID={requireNonNull(expandedEvent.value?.eventID)}
              eventSignups={signups}
              onSubmit={args => handleSubmit(signups, args.eventLabelIDs)}
              onCancel={() => bulkRemoveLabelsModalController.close()}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB: (cb) => {
        if (!busy.value) {
          highlightedEventSignups.value.clear()
          cb()
        }
      }})
    })()

    const editLabelsModalController = (() => {
      const busy = ref(false)

      const handleSubmit = async (signup: EventSignup, eventLabelIDs: Integerlike[]) : Promise<void> => {
        if (eventLabelIDs.length === 0) {
          return;
        }

        try {
          try {
            busy.value = true
            signup.labels = await updateLabelsForEventSignup(axiosAuthBackgroundInstance, {
              eventSignupID: signup.eventSignupID,
              eventSignupLabelIDs: eventLabelIDs
            })
          }
          finally {
            busy.value = false;
          }
          highlightedEventSignups.value.clear()
          editLabelsModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<EventSignup>({
        title: ModalUtil.simpleTitle("Edit Labels"),
        content: signup => {
          if (!signup) {
            return null
          }

          return <div>
            <UpdateEventSignupLabelsModal
              eventID={requireNonNull(expandedEvent.value?.eventID)}
              eventSignup={signup}
              onSubmit={args => handleSubmit(signup, args.eventLabelIDs)}
              onCancel={() => editLabelsModalController.close()}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB: (cb) => {
        if (!busy.value) {
          highlightedEventSignups.value.clear()
          cb()
        }
      }})
    })()

    const bulkAddPlayersAsEligibleToRegisterModalController = (() => {
      const {busy, onCloseCB} = useDefaultNoCloseModalIfBusy();

      const handleSubmit = async (args: {competitionUID: Guid, seasonUID: Guid, playerIDs: Guid[]}) : Promise<void> => {
        if (args.playerIDs.length === 0) {
          return; // shouldn't happen
        }

        try {
          try {
            busy.value = true

            const result = await bulkAddPlayersAsEligibleToRegister(axiosAuthBackgroundInstance, {
              competitionUID: args.competitionUID,
              seasonUID: args.seasonUID,
              playerIDs: args.playerIDs
            })

            const added = result.added === 1 ? "Added 1 player."
              : `Added ${result.added} players.`

            const notAdded = result.alreadyEligible === 0 ? ""
              : result.alreadyEligible === 1 ? "1 player was already eligible."
              : `${result.alreadyEligible} players were already eligible.`

            iziToast.success({message: `${added} ${notAdded}`})
          }
          finally {
            busy.value = false;
          }
          bulkAddPlayersAsEligibleToRegisterModalController.close()
        }
        catch (err) {
          AxiosErrorWrapper.rethrowIfNotAxiosError(err)
        }
      }

      return DefaultModalController_r<{competitionUID: Guid, players: {childID: Guid, firstName: string, lastName: string}[]}>({
        title: ModalUtil.simpleTitle("Bulk Add Players as Eligible to Register"),
        content: data => {
          if (!data) {
            return null
          }

          return <div>
              <BulkAddPlayersAsEligibleToRegisterModal
                competitionUID={data.competitionUID}
                players={data.players}
                onSubmit={args => handleSubmit({
                  competitionUID: args.competitionUID,
                  seasonUID: args.seasonUID,
                  playerIDs: args.playerIDs
                })}
                onCancel={() => bulkAddPlayersAsEligibleToRegisterModalController.close()}
              />
              <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB})
    })()

    const bulkSelectByLabelModalController = (() => {
      const labels = computed(() => {
        return expandedEvent
          .value
          ?.signups
          .flatMap(v => v.labels)
          .filter(uniqueBy(v => v.eventSignupLabelID)) ?? []
      })

      return DefaultModalController_r<void>({
        title: ModalUtil.simpleTitle("Select Signups by Label"),
        content: () => {
          return <div>
            <BulkSelectByLabelModal
              labels={labels.value}
              onSubmit={args => {
                if (args.labels.length === 0) {
                  return;
                }

                const targetLabelIDs = new Set(args.labels.map(v => v.eventSignupLabelID))

                bulkSelectedEventSignups.selected.clear()

                filteredSignups
                  .value
                  .filter(signup => {
                    return signup.labels.some(label => targetLabelIDs.has(label.eventSignupLabelID))
                  })
                  .forEach(signup => {
                    bulkSelectedEventSignups.selected.set(signup.eventSignupID, signup)
                  })

                bulkSelectByLabelModalController.close()
              }}
              onCancel={() => bulkSelectByLabelModalController.close()}
            />
          </div>
        }
      })
    })()

    const editSignupCommentsModalController = (() => {
      const busy = ref(false)

      return DefaultModalController_r<EventSignup>({
        title: ModalUtil.simpleTitle("Edit Comments"),
        content: signup => {
          if (!signup) {
            return null
          }

          return <div>
            <EditSignupCommentsModal
              text={signup.comments}
              onSubmit={async args => {
                try {
                  try {
                    busy.value = true
                    await updateSignup(axiosAuthBackgroundInstance, {eventSignupID: signup.eventSignupID, comments: args.text});
                    signup.comments = args.text;
                  }
                  finally {
                    busy.value = false
                  }
                  highlightedEventSignups.value.clear()
                  editSignupCommentsModalController.close()
                }
                catch (err) {
                  AxiosErrorWrapper.rethrowIfNotAxiosError(err);
                }
              }}
              onCancel={() => {
                highlightedEventSignups.value.clear()
                editSignupCommentsModalController.close()
              }}
            />
            <DefaultTinySoccerballBusyOverlay if={busy.value}/>
          </div>
        }
      }, {onCloseCB: (cb) => {
        if (!busy.value) {
          highlightedEventSignups.value.clear()
          cb()
        }
      }})
    })()

    return () => {
      if (!expandedEvent.value) {
        return null;
      }
      else {
        return <div style="max-width:2000px;">
          <AutoModal controller={bulkAddLabelsModalController} data-test="bulkAddLabelsModal"/>
          <AutoModal controller={bulkRemoveLabelsModalController} data-test="bulkRemoveLabelsModal"/>
          <AutoModal controller={bulkAddPlayersAsEligibleToRegisterModalController} data-test="bulkAddPlayersAsEligibleToRegisterModal"/>
          <AutoModal controller={bulkSelectByLabelModalController} data-test="bulkSelectByLabelModal"/>
          <AutoModal controller={editSignupCommentsModalController} data-test="editSignupCommentModal"/>
          <AutoModal controller={editLabelsModalController} data-test="editLabelsModal"/>
          <EventRosterImpl
            expandedEvent={expandedEvent.value}
            includeCanceled={includeCanceledSignups.value}
            highlightedEventSignups={highlightedEventSignups.value}
            selectedEventSignups={bulkSelectedEventSignups}
            bulkSelectionsNotContainedInFilteredSignups={bulkSelectionsNotContainedInFilteredSignups.value}
            labelFilterOptions={labelFilter.options}
            labelFilterSelections={labelFilter.selections}
            includeIncompleteSignups={includeIncompleteSignups}
            filteredSignups={filteredSignups.value}
            adultOrPlayerNameInput={adultOrPlayerNameInput}
            onUpdate:includeCanceled={v => {
              includeCanceledSignups.value = v;
              rehydrateFromCurrentConfig()
            }}
            onBulkAddLabels={() => bulkAddLabelsModalController.open([...bulkSelectedEventSignups.selected.values()])}
            onBulkRemoveLabels={() => bulkRemoveLabelsModalController.open([...bulkSelectedEventSignups.selected.values()])}
            onBulkAddPlayersAsEligibleToRegister={() => bulkAddPlayersAsEligibleToRegisterModalController.open({
              competitionUID: requireNonNull(expandedEvent.value).competitionUID, // expandedEvent definitely non-null here
              players: [...bulkSelectedEventSignups.selected.values()]
                .filter(v => !!v.childID) // retain those bulk selected signups that are for a player
                .filter(uniqueBy(v => v.childID)) // could have multiple signups? actually, that shouldn't happen but not hard to guarantee unqiueness here
                .map(v => {
                  return {
                    childID: v.childID,
                    firstName: v.childFirstName,
                    lastName: v.childLastName
                  }
                })
                .sort(sortByAccentAwareCaseInsenstive(v => v.lastName, v => v.firstName))
            })}
            onEditLabels={signup => {
              highlightedEventSignups.value.add(signup.eventSignupID)
              editLabelsModalController.open(signup)
            }}
            onBulkSelectByLabel={() => bulkSelectByLabelModalController.open()}
            onEditComments={signup => {
              highlightedEventSignups.value.add(signup.eventSignupID)
              editSignupCommentsModalController.open(signup)}
            }
          ></EventRosterImpl>
        </div>
      }
    }
  },
})
